import {Typography} from "@mui/material";

export const PrivacyPolicy: React.FC = () => (
    <Typography fontSize={14}>
        <style>
            {`
            table {
                width: 100%;
                border-collapse: collapse;
            }
            th, td {
                border: 1px solid black;
                padding: 8px;
                text-align: left;
            }
        `}
        </style>
        <p>
            <strong>Effective Date:</strong>15/11/2024
        </p>
        <p>
            This Privacy Policy (this “<strong>Privacy Policy</strong>”) describes the types of personal information we,
            Hydrosat Inc. and its subsidiaries and affiliates, (“<strong>Hydrosat</strong>”, “<strong>us</strong>,” “
            <strong>our</strong>“ or “<strong>we</strong>”), may collect about you in the context of your use of, and
            access to, our websites located at <a href="http://www.hydrosat.com/">www.hydrosat.com</a> and all other
            websites, mobile sites, services, applications, mobile applications, platforms and other tools where these
            terms appear or are otherwise referenced (collectively the<strong>“Website”</strong>) and the features and
            functionality thereof (the “<strong>Service(s)</strong>”), the purposes for which we use the information,
            and the circumstances in which we may share such information.
        </p>
        <p>
            When you visit the Website, we collect certain personal information about you. As used in this Privacy
            Policy, “personal information” means information that identifies, relates to, describes, is capable of being
            associated with, or could reasonably be linked, directly or indirectly, to you or your household.
        </p>
        <p>
            By using the Website, you consent to the collection and use of your personal information as described in
            this Privacy Policy, and as applicable, our Terms of Use, and as permitted or required by law. If you do not
            agree with the terms set out in this Privacy Policy, please do not use our Website or Services.
        </p>
        <p>Key Terms. It would be helpful to start by explaining some key terms used in this policy:</p>
        <table>
            <tr>
                <th>Term</th>
                <th>Description</th>
            </tr>
            <tr>
                <td>We, us, our</td>
                <td>
                    Hydrosat Inc., and its subsidiaries and affiliates, including Hydrosat S.à r.l., and Hydrosat B.V.
                </td>
            </tr>
            <tr>
                <td>The data controller according to the GDPR</td>
                <td>Hydrosat S.à.r.l. 9 Rue du Laboratoire L-1911 Luxembourg </td>
            </tr>
            <tr>
                <td>Our privacy compliance officer</td>
                <td>
                    May be contacted at{" "}
                    <a href="mailto:privacy@hydrosat.com" className="email">
                        privacy@hydrosat.com
                    </a>
                </td>
            </tr>
            <tr>
                <td>Personal information</td>
                <td>Any information relating to an identified or identifiable individual</td>
            </tr>
        </table>
        <p>
            <strong>COLLECTION OF PERSONAL INFORMATION</strong>
        </p>
        <p>
            The categories below describe the personal information we have collected in the past twelve months or may
            collect about you and the sources from which we collect those categories of personal information.
        </p>
        <table>
            <tr>
                <th>Category of Data Subject</th>
                <th>Category of Information</th>
                <th>Description</th>
                <th>Source of Information</th>
            </tr>
            <tr>
                <td>Customer, Supplier, Applicant</td>
                <td>Personal Identifiers</td>
                <td>name, email address, telephone number, postal address</td>
                <td>you</td>
            </tr>
            <tr>
                <td>Customer</td>
                <td>Account Information</td>
                <td>name, email address, password, postal address, phone number</td>
                <td>you</td>
            </tr>
            <tr>
                <td>Customer, Supplier, Applicant, Website Visitor</td>
                <td>Device and/or Online Identifiers</td>
                <td>IP address; session ID; session timestamps</td>
                <td>you; devices associated with you; third party information providers</td>
            </tr>
            <tr>
                <td>Customer, Applicant, Website Visitor</td>
                <td>Internet, Browser, and Network Activity</td>
                <td>
                    clickstream website tracking information; data related to user browser activity, including browser
                    visits, page views, visits, and unique visitors data; search history; and cookies or other similar
                    technologies
                </td>
                <td>you; devices associated with you; third party information providers</td>
            </tr>
            <tr>
                <td>Customer, Website Visitor</td>
                <td>Commercial Information</td>
                <td>information about products or services purchased, used, or considered</td>
                <td>you; third party information providers</td>
            </tr>
            <tr>
                <td>Applicant</td>
                <td>Professional or Employment Information</td>
                <td>employer and employment status</td>
                <td>you; third party information providers</td>
            </tr>
            <tr>
                <td>Applicant</td>
                <td>Education Information</td>
                <td>
                    information that is not publicly available or personally identifiable, as defined in the Family
                    Educational Rights and Privacy Act (20 U.S.C.§1232g, 34 C.F.R. Part 99)
                </td>
                <td>you; third party information providers</td>
            </tr>
            <tr>
                <td>Customer, Applicant, Website Visitor</td>
                <td>Log Information</td>
                <td>
                    information automatically recorded by our servers, including but not limited to, your Internet
                    Protocol address, operating system, browser type and language, referring URLs, access times, pages
                    viewed, links clicked, and other information about your activities on the Website
                </td>
                <td>you; devices associated with you; third party information providers</td>
            </tr>
            <tr>
                <td>Customer, Applicant, Website Visitor</td>
                <td>Mobile Device Information</td>
                <td>
                    hardware model, operating system and version, unique device identifiers, and mobile network
                    information
                </td>
                <td>devices associated with you; third party information providers</td>
            </tr>
            <tr>
                <td>Customer, Applicant, Website Visitor</td>
                <td>Cookies and Other Tracking Technology Information</td>
                <td>
                    See section below entitled “Cookies and Other Tracking Technologies” for a description of what
                    cookies or other tracking technology may be employed on the Website
                </td>
                <td>devices associated with you; third party information providers</td>
            </tr>
            <tr>
                <td>Customer, Applicant, Website Visitor</td>
                <td>Location data</td>
                <td>Region, country and city/municipality; latitude and longitude and/or geographic position data</td>
                <td>you; devices associated with you; third party information providers</td>
            </tr>
            <tr>
                <td>Customer</td>
                <td>Payment data</td>
                <td>
                    Payment card number, name on card, billing address, phone number, card security code, and expiration
                    date
                </td>
                <td>you</td>
            </tr>
        </table>
        <p>
            <strong>USE OF PERSONAL INFORMATION</strong>
        </p>
        <p>We may use your information for the following purposes:</p>
        <p>Website Visitors</p>
        <ul>
            <li>
                <p>Personalizing and tailoring the features, performance and support of the Website;</p>
            </li>
            <li>
                <p>Identifying you as a user on the Website;</p>
            </li>
            <li>
                <p>
                    Sending you promotional/marketing information, advertising, newsletters, offers or other information
                    from us;
                </p>
            </li>
            <li>
                <p>
                    Sending your promotional/marketing information, advertising, offers or other information from us
                    related to our affiliate partners;
                </p>
            </li>
            <li>
                <p>Analyzing, benchmarking and conducting research on user data and interactions with the Website;</p>
            </li>
            <li>
                <p>Monitoring, maintaining, administering and improving Website performance;</p>
            </li>
            <li>
                <p>Protecting your, our, or third parties’ networks, systems, property, or physical safety;</p>
            </li>
            <li>
                <p>
                    Complying with requests from regulatory agencies, law enforcement, and other public and government
                    authorities, as well as with relevant industry standards and policies;
                </p>
            </li>
            <li>
                <p>Complying with laws and regulations that apply to us or third parties with whom we work;</p>
            </li>
            <li>
                <p>
                    Aggregating the information collected via cookies and similar technologies to use in statistical
                    analysis to help us track trends, analyze patterns, and conduct research and product development;
                </p>
            </li>
            <li>
                <p>Meeting our or third parties’ audit and compliance requirements; and</p>
            </li>
            <li>
                <p>
                    For any other purposes that we may specifically disclose at the time you provide or we collect your
                    information.
                </p>
            </li>
        </ul>
        <p>Customers</p>
        <ul>
            <li>
                <p>Sending you communications and administrative emails;</p>
            </li>
            <li>
                <p>Personalizing and tailoring the features, performance and support of the Website;</p>
            </li>
            <li>
                <p>Identifying you as a user on the Website;</p>
            </li>
            <li>
                <p>For the performance of a contract between you and us;</p>
            </li>
            <li>
                <p>Fulfilling orders for Services or customer support requests you may place on the Website;</p>
            </li>
            <li>
                <p>
                    Sending you promotional/marketing information, advertising, newsletters, offers or other information
                    from us;
                </p>
            </li>
            <li>
                <p>
                    Sending your promotional/marketing information, advertising, offers or other information from us
                    related to our affiliate partners;
                </p>
            </li>
            <li>
                <p>Providing support related to your account or orders you may place on the Website;</p>
            </li>
            <li>
                <p>Providing you with opportunities and functionality that we think would be of particular interest;</p>
            </li>
            <li>
                <p>Analyzing, benchmarking and conducting research on user data and interactions with the Website;</p>
            </li>
            <li>
                <p>
                    Processing Services, maintaining user accounts, resolving disputes, preventing fraud and verifying
                    your identity;
                </p>
            </li>
            <li>
                <p>Monitoring, maintaining, administering and improving Website performance;</p>
            </li>
            <li>
                <p>Protecting your, our, or third parties’ networks, systems, property, or physical safety;</p>
            </li>
            <li>
                <p>
                    Complying with requests from regulatory agencies, law enforcement, and other public and government
                    authorities, as well as with relevant industry standards and policies;
                </p>
            </li>
            <li>
                <p>
                    Enforcing our contracts, terms, and conditions or otherwise exercising our legal rights; defending
                    ourselves from claims; and complying with laws and regulations that apply to us or third parties
                    with whom we work;
                </p>
            </li>
            <li>
                <p>
                    Aggregating the information collected via cookies and similar technologies to use in statistical
                    analysis to help us track trends, analyze patterns, and conduct research and product development;
                </p>
            </li>
            <li>
                <p>
                    Contacting you by email or telephone, from time to time, as part of a secondary fraud protection or
                    to solicit your feedback;
                </p>
            </li>
            <li>
                <p>Participating in any merger, acquisition, or other corporate transaction;</p>
            </li>
            <li>
                <p>Meeting our or third parties’ audit and compliance requirements; and</p>
            </li>
            <li>
                <p>
                    For any other purposes that we may specifically disclose at the time you provide or we collect your
                    information.
                </p>
            </li>
        </ul>
        <p>Suppliers</p>
        <ul>
            <li>
                <p>Sending you communications and administrative emails;</p>
            </li>
            <li>
                <p>For the performance of a contract between you and us;</p>
            </li>
            <li>
                <p>
                    Complying with requests from regulatory agencies, law enforcement, and other public and government
                    authorities, as well as with relevant industry standards and policies;
                </p>
            </li>
            <li>
                <p>
                    Enforcing our contracts, terms, and conditions or otherwise exercising our legal rights; defending
                    ourselves from claims; and complying with laws and regulations that apply to us or third parties
                    with whom we work;
                </p>
            </li>
            <li>
                <p>Meeting our or third parties’ audit and compliance requirements; and</p>
            </li>
            <li>
                <p>
                    For any other purposes that we may specifically disclose at the time you provide or we collect your
                    information.
                </p>
            </li>
        </ul>
        <p>Applicants</p>
        <ul>
            <li>
                <p>Sending you communications and administrative emails;</p>
            </li>
            <li>
                <p>Responding to your inquiries related to employment opportunities or other requests;</p>
            </li>
            <li>
                <p>Meeting our or third parties’ audit and compliance requirements; and</p>
            </li>
            <li>
                <p>
                    For any other purposes that we may specifically disclose at the time you provide or we collect your
                    information.
                </p>
            </li>
        </ul>
        <p>Anonymized or Aggregated Information</p>
        <p>
            We may also use data that we collect on an aggregate or anonymous basis (such that it does not identify any
            individual users) for various business purposes, where permissible under applicable laws and regulations,
            including for research, product development, and the purposes set forth above. We may publish or otherwise
            provide our research containing this aggregated data in our products and Services to third parties. If you
            would like to opt out of inclusion of your information in such aggregated data, please email{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            <strong>RETENTION</strong>
        </p>
        <p>
            We generally retain personal information to fulfill the purposes for which we collected it, including for
            the purposes of satisfying any legal, accounting, or reporting requirements, to establish or defend legal
            claims, or for fraud prevention purposes. To determine the appropriate retention period for personal
            information, we may consider factors such as the amount, nature, and sensitivity of the personal
            information, the potential risk of harm from unauthorized use or disclosure of your personal information,
            the purposes for which we process your personal information and whether we can achieve those purposes
            through other means, and the applicable legal requirements.
        </p>
        <p>
            When we no longer require the personal information we have collected about you, we may either delete it or
            anonymize and aggregate it. If we anonymize your personal information (so that it can no longer be
            associated with you), we may use this information indefinitely without further notice to you.
        </p>
        <p>
            <strong>SHARING OF PERSONAL INFORMATION WITH OTHER ENTITIES</strong>
        </p>
        <p>
            We may share personal information about you with other entities, including entities that provide services to
            us. We do not disclose your personal information collected through the Website to other entities, except as
            described in this Privacy Policy.
        </p>
        <p>
            Applicable law requires that we identify the categories of personal information we share for business
            purposes. We disclose the following categories of personal information for business purposes: personal
            identifiers, device and online identifiers, information about your internet, browser, and network activity,
            protected classifications, commercial information, professional or employment information, education
            information, inferences drawn from other personal information, payment data, and location data.
        </p>
        <p>
            We may share your personal information with the following categories or types of entities or in the
            following situations:
        </p>
        <p>
            <strong>Service providers: </strong>We engage service providers to facilitate our operation of the Website
            and provide the Services. These service providers analyze information about the Website and your use of the
            Website. Information disclosed for these purposes may include device and online identifiers, ISP
            information, session ID and /or session log data, information about your internet, browser, and network
            activity, and location data.
        </p>
        <p>
            <strong>Legal authorities, emergency responders and other legal requirements</strong>: We may disclose
            personal information to third parties as permitted by, or to comply with, applicable laws, regulations, or
            legal obligations, including but not limited to responding to a subpoena or similar legal process,
            protecting against fraud, cooperating with law enforcement or regulatory authorities, protecting and
            defending the rights or property of Hydrosat, preventing or investigating possible wrongdoing in connection
            with the Website/Services, protecting the personal safety of users of the service or the public, and
            protecting against legal liability. Information disclosed for these purposes may include device and online
            identifiers, information about your internet, browser, and network activity, and location data.
        </p>
        <p>
            <strong>For Business transfers: </strong>We may share or transfer your personal information in connection
            with, or during negotiations of, any merger, sale of our assets, financing, or acquisition of all or a
            portion of our business to another company.
        </p>
        <p>
            <strong>Affiliates: </strong>We may share your personal information with our affiliates, in which case we
            will require those affiliates to honor this Privacy Policy. Affiliates include our parent company and any
            other subsidiaries, joint venture partners or other companies that we control or that are under common
            control with us.
        </p>
        <p>
            <strong>With Business partners: </strong>We may share your personal information with third party companies
            that are our business partners to offer you certain products, services or promotions.
        </p>
        <p>
            <strong>With other users: </strong>when you share personal information or otherwise interact in the public
            areas of the Website with other users, such information may be viewed by all users and may be publicly
            distributed outside. If you interact with other users or register through, a third party social media
            service such as Facebook, Instagram, Twitter, or Google (“<strong>Third-Party Social Media Service</strong>
            ”), your contacts on the Third-Party Social Media Service may see your name, profile, pictures and
            description of your activity. Similarly, other users will be able to view descriptions of your activity,
            communicate with you and view your profile.
        </p>
        <p>
            <strong>User Testimonials and Feedback: </strong>We often receive testimonials and comments from users who
            have had positive experiences with our products and Services. We occasionally publish such content. When we
            publish this content, we may identify the user who submitted the content by their first and last name and
            may also indicate their home city. We obtain the user’s consent prior to posting their last name along with
            the testimonial. We may post feedback submitted to us on the Website from time to time. If you make any
            comments on a blog or forum associated with the Website, you should be aware that any personal information
            you submit there can be read, collected, or used by other users of these forums, and could be used to send
            you unsolicited messages. We are not responsible for the personally identifiable information you choose to
            submit in these blogs and forums.
        </p>
        <p>Cookies and Other Tracking Technologies</p>
        <p>
            We, our affiliates and/or other persons acting on our or their behalf use cookies and similar technology,
            such as web beacons (also known as clear GIFs or pixel tags), or similar technologies as they may develop
            over time to collect, use, and share information when you visit our Website or interact with us online, via
            email or through other electronic means Cookies are small pieces of data sent from a website and stored on a
            device. Cookies may enable us to capture and compile statistical information about how you use our Services,
            including information relating to your device’s IP address, the frequency of your visits, readership data
            (such as the average length of visits, which pages are viewed or shared during a visit or other interactions
            with our content, such as time spent viewing videos, PDFs read and links clicked), authentication
            information, acceptance or rejection of website terms, and periods of inactivity. To learn more about
            cookies, including information on what cookies have been set on your device and how cookies can be managed
            and deleted, visit <a href="https://www.allaboutcookies.org/">https://allaboutcookies.org/</a>
        </p>
        <p>
            Our Website also uses web beacons. Web beacons are invisible tags and may be placed on a webpage, in
            advertisements, or in an email or other message. They usually work in conjunction with cookies, registering
            when a particular device visits a particular page. For example, web beacons may count the number of
            individuals who visit our Website from a particular advertisement or who enroll in a service after viewing a
            particular advertisement.
        </p>
        <p>
            “Clickstream” data (e.g., information regarding which of our Website pages you access, the frequency of such
            access, and your product and service preferences) may be collected by Hydrosat itself, or by our service
            providers, using cookies, web beacons, page tags, or similar tools that are set when you visit the Website
            or when you view an advertisement we may have placed on another website. Clickstream data and similar
            information may be shared internally within Hydrosat and used for administrative purposes to assess the
            usage, value and performance of our online products and Services; to improve your experience with the
            Website; and as otherwise permitted by applicable law or regulation. This information may be processed by us
            for the purposes described above, or on our behalf by other entities, solely in accordance with our
            instructions.
        </p>
        <p>How We Use These Technologies</p>
        <p>
            We, our services providers, and third party business partners may use information collected from cookies,
            web beacons, and similar technologies for the following purposes:
        </p>
        <ul>
            <li>
                <p>
                    to allow our Services to operate as you have requested; to understand how our Services are accessed
                    and used; to recognize you when you return to our Website;
                </p>
            </li>
            <li>
                <p>to assess the effectiveness of advertising and readership content;</p>
            </li>
            <li>
                <p>
                    to deliver marketing communications that may be of interest to you, including ads or offers tailored
                    to you; and
                </p>
            </li>
            <li>
                <p>for other purposes described above under “USE OF PERSONAL INFORMATION.”</p>
            </li>
        </ul>
        <p>
            We may combine the information that we collect through cookies, web beacons and similar technologies with
            other personal information we have collected from you from both online and offline sources.
        </p>
        <p>
            We may work with service providers to promote our products and Services both on the Website and other
            websites. For example, if you visit a page on the Website or third-party websites that provides information
            about one of our products or Services, a cookie may be placed on your browser or device that identifies the
            product or service you viewed. This information allows us, our service providers, and our third-party
            business partners to deliver more relevant and tailored content, such as ads for that particular product or
            service.
        </p>
        <p>
            Other than service providers, we do not allow unaffiliated third parties to collect personal information
            about your activities on the Website.
        </p>
        <p>Managing Your Preferences</p>
        <p>
            <em>Web Browser Preferences</em>
        </p>
        <p>
            Many web browsers allow you to manage your preferences. You can set your browser to refuse cookies or delete
            certain cookies. Unless you have adjusted your browser setting so that it will reject cookies, our system
            may issue cookies when you access or use our Services. In the event you refuse to accept cookies by
            adjusting your browser setting, some or all areas of our Services may not function properly or may not be
            accessible.
        </p>
        <p>
            You can also opt out of online targeted advertising by companies participating in the Digital Advertising
            Alliance by visiting{" "}
            <a href="https://www.aboutads.info/choices" className="uri">
                https://www.aboutads.info/choices
            </a>
            .
        </p>
        <p>
            <em>Mobile Device Preferences</em>
        </p>
        <p>
            Your mobile device may give you the ability to opt out of the use of information about the mobile browsers
            or apps you use in order to serve you ads that are targeted to your interests. You can stop the collection
            of location information from your mobile device by changing the preferences on your mobile device.
        </p>
        <p>Do Not Track Policy</p>
        <p>
            Most browsers can be set to send signals to third party websites requesting them not to track the user’s
            activities. At this time, we do not respond to “do not track” signals. Consequently, our third party service
            providers may indeed track and collect information about your online activities over time while navigating
            to, from and on our Services, notwithstanding any “do not track” signals we may receive.
        </p>
        <p>Our Security and Data Storage Procedures</p>
        <p>
            Our goal is to protect your personal information submitted to us through our Services. We maintain
            reasonable physical, electronic, and procedural safeguards that comply with applicable law to guard
            nonpublic personal information about you against loss, misuse or unauthorized access, disclosure, alteration
            or destruction of the information you have provided to us through our Services. We have internal policies
            governing the proper handling of consumer information by personnel and requiring third parties that provide
            support or marketing services on our behalf to adhere to appropriate security standards with respect to such
            information. The security of your personal information is important to us, but please note that no method of
            transmission over the Internet, or method of electronic storage is perfectly secure. While we strive to use
            commercially acceptable means to protect your personal information, we cannot guarantee its absolute
            security.
        </p>
        <p>
            The Website and our Services are United States-based, and are controlled, operated and administered from our
            offices within the United States. However, Hydrosat, its operations, workforce, and service providers are
            located throughout the world. Depending on how you interact with us, your personal information may be
            handled and stored in, accessed from, or transferred to different countries. Whenever we transfer personal
            information to different countries, we take steps to ensure that the personal information we transfer
            receives adequate protection in the jurisdictions that we handle or store it.
        </p>
        <p>
            If you have additional questions about how we protect your information, please contact us at{" "}
            <a href="mailto:privacy@rocked.us">privacy@hydrosat.com.</a>
        </p>
        <p>Links to Other Websites</p>
        <p>
            This Privacy Policy only applies to the use and disclosure of information we collect through our Services.
            Our Services may contain links to other websites belonging to third parties that are not affiliated with us.
            The inclusion of any link on our Website or otherwise in our Services does not imply our endorsement of the
            linked website or service. While we try to link only to websites that meet our standards and respect for
            privacy, we are not responsible for the content, security, or privacy practices employed by other websites,
            or links posted on our Services. Information you disclose to other parties or through such sites is subject
            to the privacy and security practices and policies of those parties or websites. We disclaim all liability,
            to the extent permissible, with regard to your access to such linked websites. Access to any other websites
            is at your own discretion and we encourage you to read the privacy statements of each and every website or
            Services visited in order to learn how such third parties may treat your information.
        </p>
        <p>Information Collected from Third Party Companies</p>
        <p>
            We may receive personal information and/or anonymous data about you from companies that provide our Services
            or products by way of a co-branded or private-labeled website or companies that offer their products and/or
            services on the Website. We may add this information to the personal information we have already collected
            from you via the Website or Services in order to improve the Services we provide, the features we offer you,
            or the functionality of the Website.
        </p>
        <p>Email Communications</p>
        <p>
            We will periodically send you free newsletters and e-mails that directly promote our products, our business
            partners’ products, the use of our Website or the Services. When you receive newsletters or promotional
            communications from us, you may indicate a preference to stop receiving further communications from us and
            you will have the opportunity to “opt-out” by following the unsubscribe instructions provided in the e-mail
            you receive or by contacting us directly (please see contact information below). Withdrawing your consent
            for one mailing list will not affect your receipt of messages from other mailing lists or of information
            concerning your purchases. Despite your indicated e-mail preferences, we may send you service related or
            administrative communications, including notices of any updates to our Terms of Use or Privacy Policy.
        </p>
        <p>
            <strong>Detailed Information on the Processing of Your Personal Information</strong>
        </p>
        <p>
            Our third-party service providers have access to your personal information only to perform their tasks on
            our behalf and are obligated not to disclose or use it for any other purpose.
        </p>
        <p>Website</p>
        <p>
            We use Amazon Web Services (“AWS”) to power our Website. You can read more about how AWS uses your Personal
            Information here:{" "}
            <a href="https://aws.amazon.com/privacy/" className="uri">
                https://aws.amazon.com/privacy/
            </a>
        </p>
        <p>
            We use AWS Pinpoint to engage with our Website users and customers. You can read more about how AWS uses
            your Personal Information here:{" "}
            <a href="https://aws.amazon.com/privacy/" className="uri">
                https://aws.amazon.com/privacy/
            </a>
        </p>
        <p>
            We use Google Analytics to help us understand how our customers use our Website. You can read more about how
            Google uses your Personal Information here:{" "}
            <a href="https://policies.google.com/privacy" className="uri">
                https://policies.google.com/privacy
            </a>
            . You can also opt-out of Google Analytics here:{" "}
            <a href="https://tools.google.com/dlpage/gaoptout" className="uri">
                https://tools.google.com/dlpage/gaoptout
            </a>
            .
        </p>
        <p>
            We use YouTube to host and serve content on our Website. You can read more about how Google, the operator of
            YouTube, uses your Personal Information here:{" "}
            <a href="https://policies.google.com/privacy" className="uri">
                https://policies.google.com/privacy
            </a>
            .
        </p>
        <p>Social Logins</p>
        <p>
            Social login is a form of single sign-on that allows you to create a new account or sign into your account
            on our Website using your existing login information from a social-networking service such as Facebook or
            Google or Twitter instead of creating a new login account specifically for our Website. It is designed to
            simplify logins.
        </p>
        <p>
            You don’t have to use a social login. We offer registration that uses your email address instead and is not
            linked with an existing social-networking account.
        </p>
        <p>
            If you register or access your account on our Website with a social networking service (or later link your
            account to one), we will collect certain information about you from that social networking service, and what
            we collect depends on your privacy settings with that social networking service.
        </p>
        <p>For more information about these services and their privacy policies, please visit their websites here:</p>
        <p>
            Google:{" "}
            <a href="https://policies.google.com/privacy?hl=en-US" className="uri">
                https://policies.google.com/privacy?hl=en-US
            </a>
        </p>
        <p>
            LinkedIn:{" "}
            <a href="https://www.linkedin.com/legal/privacy-policy" className="uri">
                https://www.linkedin.com/legal/privacy-policy
            </a>
        </p>
        <p>
            X (Twitter):{" "}
            <a href="https://twitter.com/en/privacy" className="uri">
                https://twitter.com/en/privacy
            </a>
        </p>
        <p>Mobile Apps</p>
        <p>
            We use Apple’s App Store and the Google Play Store to offer and distribute our mobile app. You can learn
            more about how Apple and Google use your Personal Information here:
        </p>
        <p>
            Apple:{" "}
            <a href="https://support.apple.com/en-us/HT211970" className="uri">
                https://support.apple.com/en-us/HT211970
            </a>
        </p>
        <p>
            Google:{" "}
            <a href="https://policies.google.com/privacy?hl=en-US" className="uri">
                https://policies.google.com/privacy?hl=en-US
            </a>
        </p>
        <p>Email Marketing</p>
        <p>
            We may use third party email marketing service providers to manage and send emails to you. We may use your
            personal information to contact you with newsletters, marketing or promotional materials and other
            information that may be of interest to you. You may opt-out of receiving any, or all, of these
            communications from us by following the unsubscribe link or instructions provided in any email we send or by
            contacting us. Third party services that we used or may use in the future include MailChimp. For more
            information about these services and their privacy policies, please visit their websites.
        </p>
        <p>
            Mailchimp:{" "}
            <a href="https://www.intuit.com/privacy/statement/" className="uri">
                https://www.intuit.com/privacy/statement/
            </a>
        </p>
        <p>Payments</p>
        <p>
            We may provide paid products and/or services within the Website. In that case, we may use third-party
            services for payment processing (e.g. payment processors). We will not store or collect your payment card
            details. That information is provided directly to our third-party payment processors whose use of your
            personal information is governed by their own privacy policies. These payment processors adhere to the
            standards set by PCI-DSS as managed by the PCI Security Standards Council, which is a joint effort of brands
            like Visa, Mastercard, American Express and Discover. PCI-DSS requirements help ensure the secure handling
            of payment information.
        </p>
        <p>Behavioral Advertising</p>
        <p>
            As described above, we use your personal information to provide you with targeted advertisements or
            marketing communications we believe may be of interest to you. For more information about how targeted
            advertising works, you can visit the Network Advertising Initiative’s (NAI) educational page at{" "}
            <a href="http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work">
                http://www.networkadvertising.org/understanding-online-advertising/how-does-it-work.
            </a>
        </p>
        <p>
            We may use the following providers for such advertising and you can opt out of targeted advertising by using
            the links below:
        </p>
        <p>
            Google AdSense &amp; DoubleClick Cookie. Google, as a third party vendor, uses cookies to serve ads related
            to our Service. Google’s use of the DoubleClick cookie enables it and its partners to serve ads to our users
            based on their visit to our Service or other websites on the Internet. You may opt out of the use of the
            DoubleClick Cookie for interest-based advertising by visiting the Google Ads Settings web page:{" "}
            <a href="http://www.google.com/ads/preferences/" className="uri">
                http://www.google.com/ads/preferences/
            </a>
            . You can learn more about how Google uses your personal information here:{" "}
            <a href="https://policies.google.com/privacy" className="uri">
                https://policies.google.com/privacy
            </a>
            .
        </p>
        <p>
            X (Twitter). Privacy controls: (
            <a href="https://help.twitter.com/en/safety-and-security/privacy-controls-for-tailored-ads" className="uri">
                https://help.twitter.com/en/safety-and-security/privacy-controls-for-tailored-ads
            </a>
            ). You can learn more about how X uses your personal information here:{" "}
            <a href="https://twitter.com/en/privacy" className="uri">
                https://twitter.com/en/privacy
            </a>
            .
        </p>
        <p>
            Additionally, you can opt out of some of these services by visiting the Digital Advertising Alliance’s
            opt-out portal at:{" "}
            <a href="http://optout.aboutads.info/" className="uri">
                http://optout.aboutads.info/
            </a>
            .
        </p>
        <p>
            Please note that any opt out is specific to the browser you use. You may need to opt out on every browser on
            every device that you use.
        </p>
        <p>
            <strong>PROTECTING CHILDREN’S PRIVACY ONLINE</strong>
        </p>
        <p>
            Protecting the privacy of minors is important to Hydrosat. We comply with the Children’s Online Privacy
            Protection Act in the United States, and similar laws around the world, where applicable. The Website, and
            the Services are not directed to or intended for individuals under 18 years of age. We do not knowingly
            collect or solicit personal information from or about individuals under the age of 18, or the relevant
            minimum age under applicable local legal requirements, except as permitted under applicable law. If you
            become aware that we have collected personal information from a minor, please contact us at{" "}
            <a href="mailto:privacy@rocked.us">privacy@hydrosat.com</a>so that we can promptly investigate and address
            the issue.
        </p>
        <p>
            <strong>CALIFORNIA RESIDENTS</strong>
        </p>
        <p>
            California residents should be aware that this section does not apply to personal information covered by
            certain sector-specific privacy laws, including the Fair Credit Reporting Act (FCRA), the Gramm-Leach-Bliley
            Act (GLBA) and its implementing regulations, the California Financial Information Privacy Act (FIPA), and
            the Driver’s Privacy Protection Act of 1994; or other information subject to a California Consumer Privacy
            Act (CCPA) exception.
        </p>
        <p>Your Rights under the CCPA</p>
        <p>
            Under this Privacy Policy, and by law if you are a resident of California, you may have the following
            rights: The right to notice. You must be properly notified which categories of personal information are
            being collected and the purposes for which the personal information is being used.
        </p>
        <p>
            <strong>The right to access / the right to request. </strong>The CCPA permits you to request and obtain from
            us information regarding the disclosure of your personal information that has been collected in the past 12
            months by us or its subsidiaries to a third-party for the third party’s direct marketing purposes.
        </p>
        <p>
            <strong>The right to say no to the sale of personal information. </strong>You also have the right to ask us
            not to sell your personal information to third parties.
        </p>
        <p>
            <strong>The right to know about your personal information. </strong>You have the right to request and obtain
            from Hydrosat information regarding the disclosure of the following:
        </p>
        <ul>
            <li>
                <p>The categories of personal information collected</p>
            </li>
            <li>
                <p>The sources from which the personal information was collected</p>
            </li>
            <li>
                <p>The business or commercial purpose for collecting or selling the personal information</p>
            </li>
            <li>
                <p>Categories of third parties with whom we share personal information</p>
            </li>
            <li>
                <p>The specific pieces of personal information we collected about you</p>
            </li>
        </ul>
        <p>
            <strong>The right to correct inaccuracies in your personal information. </strong>You are entitled to request
            that any incomplete or inaccurate information we hold about you be corrected.
        </p>
        <p>
            <strong>The right to delete personal information. </strong>You also have the right to request the deletion
            of your personal information that has been collected in the past 12 months.
        </p>
        <p>
            <strong>The right to limit the use of your sensitive personal information</strong>. You have the right to
            request that the use of your sensitive personal information be limited to only those uses that are essential
            for providing goods and services.
        </p>
        <p>
            <strong>The right not to be discriminated against. </strong>You have the right not to be discriminated
            against for exercising any of your consumer rights, including by:
        </p>
        <ul>
            <li>
                <p>Denying goods or services to you</p>
            </li>
            <li>
                <p>
                    Charging different prices or rates for goods or services, including the use of discounts or other
                    benefits or imposing penalties
                </p>
            </li>
            <li>
                <p>Providing a different level or quality of goods or services to you</p>
            </li>
            <li>
                <p>
                    Suggesting that you will receive a different price or rate for goods or services or a different
                    level or quality of goods or services.
                </p>
            </li>
        </ul>
        <p>Exercising Your CCPA Data Protection Rights</p>
        <p>
            In order to exercise any of your rights under the CCPA, and if you are a California resident, you can email
            us at{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            California law requires us to verify the requests we receive from you when you exercise certain of the
            rights listed above. To verify your request, we will check the information you provide us in your request
            against third party identity verification tools. As part of this process, we may call you after you submit
            your request to verify information. You may also designate an authorized agent to exercise certain of the
            rights listed above on your behalf by providing the authorized agent with power of attorney pursuant to the
            California Probate Code. If an authorized agent submits a request on your behalf, we will contact you to
            verify that they represent you.
        </p>
        <p>
            Hydrosat will disclose and deliver the required information free of charge within 45 days of receiving your
            verifiable request. The time period to provide the required information may be extended once by an
            additional 45 days when reasonably necessary and with prior notice.
        </p>
        <p>Do Not Sell My Personal Information</p>
        <p>
            We do not sell, and have not within the last twelve months sold, your personal information. However, the
            third party service providers we partner with (for example, our advertising partners) may use technology on
            the Website that may be considered or constitute a “sale” of personal information as defined by the CCPA
            law. If you are a California resident and wish to opt out of the sale of your personal information as
            defined under CCPA law, you may do so by emailing us at{" "}
            <a href="mailto:privacy@rocked.us">privacy@hydrosat.com.</a>
        </p>
        <p>“Do Not Track” Policy as Required by California Online Privacy Protection Act (CalOPPA)</p>
        <p>
            Our Website does not respond to Do Not Track signals. However, some third-party websites do keep track of
            your browsing activities. If you are visiting such websites, you can set your preferences in your web
            browser to inform websites that you do not want to be tracked. You can enable or disable Do Not Track
            signals by visiting the preferences or settings page of Your web browser.
        </p>
        <p>Your California Privacy Rights (California’s Shine the Light law)</p>
        <p>
            Under California Civil Code Section 1798 (California’s Shine the Light law), California residents with an
            established business relationship with us can request information once a year about sharing their personal
            information with third parties for the third parties’ direct marketing purposes. If you’d like to request
            more information under the California Shine the Light law, and if you are a California resident, you can
            contact us by email at{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            <strong>COLORADO RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Colorado, according to the “Colorado Privacy Act” (also known as “<strong>CPA</strong>”), and, for
            such consumers, these provisions supersede any other possibly divergent or conflicting provisions contained
            in the Privacy Policy. The CPA gives Colorado consumers the right to (i) opt-out from the sale of their
            personal data, or use of personal data for targeted advertising and certain types of profiling; (ii) (ii)
            know whether a controller is collecting personal data; (iii) to access, correct or delete personal data that
            a controller has collected about them; and (iv) to download and remove personal data from a platform in a
            format that allows the transfer to another platform. If you are a Colorado consumer and wish to exercise
            these rights, please submit your request by emailing us at{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            <strong>CONNECTICUT RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Connecticut, according to “An Act Concerning Personal Data Privacy and Online Monitoring” (also
            known as “The Connecticut Data Privacy Act” or the “<strong>CTDPA</strong>”), and, for such consumers, these
            provisions supersede any other possibly divergent or conflicting provisions contained in the Privacy Policy.
            This part of the document uses the term “personal data” as defined in the CTDPA. Connecticut law gives
            Connecticut consumers the right to: (i) access, correct, delete, or obtain a copy of their personal
            information in a portable format, and (ii) opt out of the processing of your personal data for the purposes
            of targeted advertising, the sale of personal data, or profiling in furtherance of decisions that produce
            legal or similarly significant effects concerning you. If you are a Connecticut consumer and wish to
            exercise these rights, please submit your request by emailing us at{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            <strong>DELAWARE RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Delaware, according to the “Delaware Personal Data Privacy Act” (also known as the “Delaware
            Privacy Law” or the “<strong>DPDPA</strong>”), and, for such consumers, these provisions supersede any other
            possibly divergent or conflicting provisions contained in the Privacy Policy. This part of the document uses
            the term “personal data” as defined in the DPDPA. Delaware law gives Delaware consumers the right to: (i)
            confirm whether a controller is processing the consumer’s personal data and access such personal data; (ii)
            correct inaccuracies in the consumer’s personal data, taking into account the nature of the personal data
            and the purposes of the processing of the consumer’s personal data; (iii) delete personal data provided by,
            or obtained about, the consumer; (iv) obtain a copy of the consumer’s personal data processed by the
            controller, in a portable and, to the extent technically feasible, readily usable format that allows the
            consumer to transmit the data to another controller without hindrance, where the processing is carried out
            by automated means; (v) obtain a list of the specific third parties to which the controller has disclosed
            the consumer’s personal data; and (vi) opt out of the processing of personal data for the purposes of
            targeted advertising, the sale of personal data, or profiling in furtherance of solely automated decisions
            that produce legal or similarly significant effects concerning the consumer. If you are a Delaware consumer
            and wish to exercise these rights, please submit your request by emailing us at{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            <strong>FLORIDA RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Florida, according to the “Florida Digital Bill of Rights” (also known as the “Florida Privacy Law”
            or the “<strong>FDBR</strong>”), and, for such consumers, these provisions supersede any other possibly
            divergent or conflicting provisions contained in the Privacy Policy. This part of the document uses the term
            “personal data” as defined in the FDBR The FDBR gives Florida consumers the right to: (i) confirm whether
            the controller is processing their personal data and provide them access to their personal data; (ii)
            correct inaccuracies in the consumer’s personal data, taking into account the nature of the personal data,
            and the purposes of the processing of the consumer’s personal data; (iii) delete personal data provided by,
            or obtained about, the consumer; (iv) obtain a copy of the consumer’s personal data in a portable and, to
            the extent technically feasible, readily usable format if the data is available in a digital format; (v) opt
            out of the processing of their personal data for targeted advertising, the sale of their personal data, or
            profilingin furtherance of a decision that produces a legal or similarly significant effect concerning a
            consumer; and (vi) opt out of the collection or processing of sensitive data, including precise geolocation
            data. If you are a Florida consumer and wish to exercise these rights, please submit your request by
            emailing us at{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            <strong>INDIANA RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Indiana, according to the “Indiana Consumer Data Protection Act” (also known as the “Indiana Data
            Privacy Law” or the “<strong>INCDPA</strong>”), and, for such consumers, these provisions supersede any
            other possibly divergent or conflicting provisions contained in the Privacy Policy. This part of the
            document uses the term “personal data” as defined in the INCDPA The INCDPA gives Indiana consumers the right
            to: (i) confirm whether or not a controller is processing the consumer’s personal data; (ii) correct
            inaccuracies in the consumer’s personal data that the consumer previously provided to a controller, taking
            into account the nature of the personal data and the purposes of the processing of the consumer’s personal
            data; (iii) delete personal data provided by, or obtained about, the consumer; (iv) obtain either a copy of;
            a representative summary of the consumer’s personal data that the consumer previously provided to the
            controller in a portable and, to the extent technically practicable, readily usable format; and (v) opt out
            of the processing of their personal data for targeted advertising, the sale of their personal data, or
            profilingin furtherance of a decision that produces a legal or similarly significant effect concerning a
            consumer. If you are an Indiana consumer and wish to exercise these rights, please submit your request by
            emailing us at{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            <strong>IOWA RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Iowa, according to the “Iowa Act Relating to Consumer Data Protection” (also known as the “Iowa
            Data Privacy Law” or the “<strong>ICDPA</strong>”), and, for such consumers, these provisions supersede any
            other possibly divergent or conflicting provisions contained in the Privacy Policy. This part of the
            document uses the term “personal data” as defined in the ICDPA The ICDPA gives Iowa consumers the right to:
            (i) confirm whether or not a controller is processing the consumer’s personal data and access such data;
            (ii) delete personal data provided by the consumer; (iii) obtain a copy of the consumer’s personal data that
            the consumer previously provided to the controller in a portable and, to the extent technically practicable,
            readily usable format that allows the consumer to transmit the data to another controller; (iv) opt out of
            the sale personal data. If you are an Iowa consumer and wish to exercise these rights, please submit your
            request by emailing us at{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            <strong>MONTANA RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Montana, according to the “Montana Consumer Data Privacy Act” (also known as the “Montana Privacy
            Law” or the “<strong>MCDPA</strong>”), and, for such consumers, these provisions supersede any other
            possibly divergent or conflicting provisions contained in the Privacy Policy. The MCDPA gives Montana
            consumers the right (i) confirm whether the controller is processing the consumer’s data and provide access
            to the consumer’s data; (ii) correct inaccurate personal data of the consumer; (iii) request deletion of
            their personal data; (iv) obtain a portable copy of their personal data; and (v) opt out of the processing
            of the consumer’s personal data for purposes of targeted advertising, the sale of personal data, or
            profiling in furtherance of solely automated decisions that produce legal or similarly significant effects
            concerning the consumer. If you are a Montana consumer and wish to exercise these rights, please submit your
            request by emailing us at{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
            .
        </p>
        <p>
            <strong>NEVADA RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Nevada, according to the “Nevada Privacy of Information Collected on the Internet from Consumers
            Act” (also known as the “Nevada Privacy Law” or the “<strong>NPICICA</strong>”), and, for such consumers,
            these provisions supersede any other possibly divergent or conflicting provisions contained in the Privacy
            Policy. The NPICICA gives Nevada consumers the right to request that a company not sell their personal
            information. If you are a Nevada consumer and wish to exercise these rights, please submit your request by
            emailing us at <a href="mailto:privacy@rocked.us">privacy@hydrosat.com.</a>
        </p>
        <p>
            <strong>OREGON RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Oregon, according to “<strong>Oregon Consumer Privacy Act</strong>”, and, for such consumers, these
            provisions supersede any other possibly divergent or conflicting provisions contained in the Privacy Policy.
            The Oregon Consumer Privacy Act gives Oregon consumers the right (i) to know that a controller is processing
            or has processed their personal data; the categories of personal data the controller is processing or has
            processed; and a list of specific third parties for which data has been disclosed; (ii) to correct
            inaccuracies in personal data about the consumer, taking into account the nature of the personal data and
            purpose for processing the personal data; (iii) delete personal data about the consumer, including personal
            data the consumer provided to the controller, personal data the controller obtained from another source and
            derived data; (iv) to opt-out of the processing of their personal data for targeted advertising, sales or
            profiling of the consumer in furtherance of decisions that produce legal effects or effects of similar
            significance; and (v) to obtain a portable and readily usable copy of their personal data. If you are an
            Oregon consumer and wish to exercise these rights, please submit your request by emailing us at{" "}
            <a href="mailto:privacy@rocked.us">privacy@hydrosat.com.</a>
        </p>
        <p>
            <strong>TEXAS RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Texas, according to “Texas Data Privacy and Security Act” (also known as the “Texas Privacy Law” or
            the “<strong>TDPSA</strong>”), and, for such consumers, these provisions supersede any other possibly
            divergent or conflicting provisions contained in the Privacy Policy. The TDSPA gives Texas consumers the
            right (i) confirm whether a controller is processing their personal data and access that data; (ii) correct
            inaccuracies in their personal data; (iii) request deletion of their data (whether the personal data was
            provided by the consumer or obtained about the consumer); (iv) obtain a portable copy of their personal
            data; (v) opt out of processing for the purposes of targeted advertising, sale of personal data or
            profiling; and (vi) appeal a controller’s refusal to take action on a consumer request to exercise their
            rights.. If you are a Texas consumer and wish to exercise these rights, please submit your request by
            emailing us at <a href="mailto:privacy@rocked.us">privacy@hydrosat.com.</a>
        </p>
        <p>
            <strong>UTAH RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Utah, according to the “Utah Consumer Privacy Act” (“UCPA”), and, for such consumers, these
            provisions supersede any other possibly divergent or conflicting provisions contained in the Privacy Policy.
            The UCPA gives Utah consumers the right to (i) access, delete, or obtain a copy of their personal
            information in a portable format, and (ii) opt out of the processing of your personal data for the purposes
            of targeted advertising, the sale of personal data. If you are a Utah consumer and wish to exercise these
            rights, please submit your request by emailing us at{" "}
            <a href="mailto:privacy@rocked.us">privacy@hydrosat.com.</a>
        </p>
        <p>
            <strong>VIRGINIA RESIDENTS</strong>
        </p>
        <p>
            This part of the document integrates with and supplements the information contained in the rest of the
            Privacy Policy. The provisions contained in this section apply to all who are consumers residing in the
            State of Virginia, according to the “Virginia Consumer Data Protection Act” (“<strong>VCDPA</strong>”), and,
            for such consumers, these provisions supersede any other possibly divergent or conflicting provisions
            contained in the Privacy Policy. The VCDPA gives Virginia consumers the right to (i) access, correct,
            delete, or obtain a copy of their personal information in a portable format, (ii) to request that a company
            not sell their personal information, (iii) to opt- out of the processing of their personal information for
            any targeted advertising, and (iv) not be discriminated against for exercising any of the data privacy
            rights granted. If you are a Virginia consumer and wish to exercise these rights, please submit your request
            by emailing us at <a href="mailto:privacy@rocked.us">privacy@hydrosat.com.</a>
        </p>
        <p>
            <strong>INTERNATIONAL USERS</strong>
        </p>
        <p>
            Our Website and the Services are United States-based and are controlled, operated and administered by
            Hydrosat from its offices within the United States of America, and are made available to you on an
            unsolicited basis, without any marketing or promotion regarding these Services to you from Hydrosat or its
            personnel. This Privacy Policy is provided in accordance with and subject to applicable U.S. law. If you
            decide to continue to access our Services from your location outside the United States, you hereby agree
            that your use of the Services is subject to this Privacy Policy and your personal information will be
            transferred to or processed within the United States.
        </p>
        <p>
            <strong>EUROPEAN ECONONOMIC AREA (“EEA”) USERS</strong>
        </p>
        <p>
            <strong>GDPR Privacy Notice: </strong>
            <em>
                This notice is for people who are located in the European Economic Area (<strong>“EEA”</strong>). Our
                processing of personal data of people who are in the EEA is governed by the General Data Protection
                Regulation (the <strong>“GDPR”</strong>). The GDPR requires us to provide certain information to you
                about your personal data, which we refer to in this notice as your personal information.
            </em>
        </p>
        <p>Legal Basis for Processing Personal Information under GDPR</p>
        <p>We may process personal information under the following conditions:</p>
        <ul>
            <li>
                <p>
                    Consent - you have given your consent for processing personal information for one or more specific
                    purposes.
                </p>
            </li>
            <li>
                <p>
                    Performance of a contract - provision of personal information is necessary for the performance of an
                    agreement with you and/or for any pre-contractual obligations thereof.
                </p>
            </li>
            <li>
                <p>
                    Legal obligations - processing personal information is necessary for compliance with a legal
                    obligation to which we are subject.
                </p>
            </li>
            <li>
                <p>
                    Vital interests - processing personal information is necessary in order to protect your vital
                    interests or of another natural person.
                </p>
            </li>
            <li>
                <p>
                    Legitimate interests - processing personal information is necessary for the purposes of the
                    legitimate interests pursued by Hydrosat.
                </p>
            </li>
        </ul>
        <p>
            The legal bases for processing apply to the various categories of personal information processed as follows:
        </p>
        <table>
            <tr>
                <th>Category of Information</th>
                <th>Legal Basis for Processing</th>
            </tr>
            <tr>
                <td>Personal Identifiers</td>
                <td>Consent, Performance of a contract, Legal obligations, Vital interests, Legitimate interests</td>
            </tr>
            <tr>
                <td>Account Information</td>
                <td>Consent, Performance of a contract, Legal obligations, Legitimate interests</td>
            </tr>
            <tr>
                <td>Device and/or Online Identifiers</td>
                <td>Vital interests, Legitimate interests</td>
            </tr>
            <tr>
                <td>Internet, Browser, and Network Activity</td>
                <td>Consent, Vital interests, Legitimate interests</td>
            </tr>
            <tr>
                <td>Commercial Information</td>
                <td>Performance of a contract, Legitimate interests</td>
            </tr>
            <tr>
                <td>Professional or Employment Information</td>
                <td>Consent, Performance of a contract, Legal obligations, Legitimate interests</td>
            </tr>
            <tr>
                <td>Education Information</td>
                <td>Consent, Performance of a contract, Legal obligations, Legitimate interests</td>
            </tr>
            <tr>
                <td>Log Information</td>
                <td>Consent, Vital interests, Legitimate interests</td>
            </tr>
            <tr>
                <td>Mobile Device Information</td>
                <td>Consent, Vital interests, Legitimate interests</td>
            </tr>
            <tr>
                <td>Cookies and Other Tracking Technology Information</td>
                <td>Consent, Legitimate interests</td>
            </tr>
            <tr>
                <td>Location data</td>
                <td>Consent, Legitimate interests</td>
            </tr>
        </table>
        <p>
            In any case, Hydrosat will gladly help to clarify the specific legal basis that applies to the processing,
            and in particular whether the provision of personal information is a statutory or contractual requirement,
            or a requirement necessary to enter into a contract.
        </p>
        <p>Your Rights under the GDPR</p>
        <p>
            Hydrosat undertakes to respect the confidentiality of your personal information and to enable you to
            exercise your rights under the GDPR.
        </p>
        <p>You have the right under this Privacy Policy, and by law if you are within the EEA to:</p>
        <ul>
            <li>
                <p>
                    <strong>Request access to your personal information. </strong>The right to access, update or delete
                    the information we have on you. Whenever made possible, you can access, update or request deletion
                    of your personal information directly within your account settings. If you are unable to perform
                    these actions yourself, please contact us by email at{" "}
                    <a href="mailto:privacy@rocked.us">privacy@hydrosat.com</a>to assist you. This also enables you to
                    receive a copy of the personal information we hold about you.
                </p>
            </li>
            <li>
                <p>
                    <strong>Request correction of the personal information that we hold about you</strong>. You have the
                    right to have any incomplete or inaccurate information we hold about you corrected.
                </p>
            </li>
            <li>
                <p>
                    <strong>Object to processing of your personal information</strong>.This right exists where we are
                    relying on a legitimate interest as the legal basis for our processing and there is something about
                    your particular situation, which makes you want to object to our processing of your personal
                    information on this ground. You also have the right to object where we are processing your personal
                    information for direct marketing purposes.
                </p>
            </li>
            <li>
                <p>
                    <strong>Request erasure of Your personal information</strong>. You have the right to ask us to
                    delete or remove personal information when there is no good reason for us to continue processing it.
                </p>
            </li>
            <li>
                <p>
                    <strong>Request the transfer of Your personal information</strong>. We will provide to you, or to a
                    third-party you have chosen, your personal information in a structured, commonly used,
                    machine-readable form. Please note that this right only applies to automated information which you
                    initially provided consent for us to use or where we used the information to perform a contract with
                    you.
                </p>
            </li>
            <li>
                <p>
                    <strong>Withdraw Your Consent</strong>.You have the right to withdraw your consent on using your
                    personal information. If you withdraw your consent, we may not be able to provide you with access to
                    certain specific functionalities of the Website.
                </p>
            </li>
        </ul>
        <p>Exercising of Your GDPR Data Protection Rights</p>
        <p>
            You may exercise your rights of access, rectification, cancellation and opposition by contacting us by email
            at <a href="mailto:privacy@rocked.us">privacy@hydrosat.com. </a>Please note that we may ask you to verify
            your identity before responding to such requests. If you make a request, we will try our best to respond to
            you as soon as possible.
        </p>
        <p>
            You have the right to complain to a Data Protection Authority about our collection and use of your personal
            information. For more information, if you are in the EEA, please contact your local data protection
            authority in the EEA. You can find an overview of all data protection authorities in the EU here:{" "}
            <a href="https://www.edpb.europa.eu/about-edpb/about-edpb/members_en#member-nl">
                EU Data Protection Authorities
            </a>
        </p>
        <p>Contacting Our GDPR Privacy Compliance Officer</p>
        <p>
            We have designated a privacy compliance officer whom you have the right to contact with regard to all issues
            related to processing of your personal information and to the exercise of your rights under the GDPR. You
            may contact our privacy compliance officer by email at{" "}
            <a href="mailto:privacy@rocked.us">privacy@hydrosat.com.</a>
        </p>
        <p>
            EEA data subjects may contact our privacy compliance officer with regard to all issues related to processing
            of their personal data and to the exercise of their rights under the GDPR.
        </p>
        <p>
            <strong>SECURITY MEASURES</strong>
        </p>
        <p>
            We implement reasonable security practices, including encryption, access controls, and regular audits, to
            safeguard your personal data. However, no method of transmission over the internet or method of electronic
            storage is completely secure, and we cannot guarantee absolute security.
        </p>
        <p>
            <strong>INTERNATIONAL DATA TRANSFERS</strong>
        </p>
        <p>
            For users outside the United States, please note that your personal information may be transferred to, and
            processed in, the United States or other countries where Hydrosat and its service providers operate. We
            implement safeguards to ensure your personal information remains protected when transferred internationally.
        </p>
        <p>
            <strong>EMAIL COMMUNICATIONS AND MARKETING</strong>
        </p>
        <p>
            You may unsubscribe from promotional emails at any time by following the instructions in each email.
            However, you may still receive important administrative communications, including notices about changes to
            our policies.
        </p>
        <p>
            <strong>CHANGES TO THIS PRIVACY POLICY</strong>
        </p>
        <p>
            We reserve the right to modify or supplement this Privacy Policy at any time. Therefore, we recommend that
            you review this Privacy Policy, updated and posted on this Website, regularly for changes. The effective
            date of this Privacy Policy, as stated above, indicates the last time this Privacy Policy was revised.
        </p>
        <p>
            Your continued use of our Services after we have changed the Privacy Policy signifies your acceptance of the
            revised terms. This Privacy Policy may be changed from time to time to reflect changes in our practices
            concerning the collection and use of personal information related to your use of the Website. Under certain
            circumstances (i.e., with respect to material changes to this Privacy Policy), we may also elect to notify
            you through additional means, such as by posting a notice on the Website or sending you an email. The
            revised Privacy Policy will be effective immediately upon posting to our Website.
        </p>
        <p>
            <strong>OUR OTHER PRIVACY POLICIES OR NOTICES</strong>
        </p>
        <p>
            This Privacy Policy provides a general statement of the ways in which Hydrosat protects your personal
            information when you access and use this Website. You may, however, in connection with specific products or
            services offered by Hydrosat, be provided with separate privacy policies or notices that describe the
            privacy practices associated with your use of those products or services.
        </p>
        <p>
            <strong>CONTACT US</strong>
        </p>
        <p>If you have any questions about this Privacy Policy, you can contact us:</p>
        <p>
            By email:{" "}
            <a href="mailto:privacy@hydrosat.com" className="email">
                privacy@hydrosat.com
            </a>
        </p>
        <p>By mail: For Non-EU residents:</p>
        <p>Hydrosat Inc.</p>
        <p>1250 Connecticut Ave NW, Ste 700-09</p>
        <p>Washington, DC 20036</p>
        <p>ATTN: Privacy Compliance Officer</p>
        <p>For EU residents:</p>
        <p>Hydrosat S.à.r.l.</p>
        <p>9 Rue du Laboratoire L-1911</p>
        <p>Luxembourg</p>
        <p>ATTN: Privacy Compliance Officer</p>
    </Typography>
);
